import React, { useRef, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import './PrintLead.css';
import { useLanguage } from '../../i18n/LanguageContext';
import { translatePropertyType } from '../../i18n/translations';

const PrintLead = () => {
  const location = useLocation();
  const history = useHistory();
  const componentRef = useRef();
  const { t, language } = useLanguage();
  
  // Parse URL parameters
  const params = new URLSearchParams(location.search);
  const address = params.get('address') || '';
  const phone = params.get('phone') || '';
  const email = params.get('email') || '';
  const propertyType = params.get('propertyType') || '';
  const numRooms = params.get('numRooms') || '';
  const area = params.get('area') || '';
  const floor = params.get('floor') || '';
  const condition = params.get('condition') || '';
  const constructionYear = params.get('constructionYear') || '';
  const monthlyFee = params.get('monthlyFee') || '';
  const plotSize = params.get('plotSize') || '';
  const estimatedValue = params.get('estimatedValue') || '';
  const explanation = params.get('explanation') || '';
  const ownership = params.get('ownership') || '';
  const monthlyFeeSuffix = language === 'dk' ? 'kr./md.' : language === 'no' ? 'kr/mnd.' : 'kr/mån';

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: t('printLead.documentTitle', { address }),
  });

  const handleGoBack = () => {
    history.push('/');
  };

  return (
    <div className="print-lead-container">
      <div className="print-lead-actions no-print">
        <button className="back-button" onClick={handleGoBack}>
          <FontAwesomeIcon icon={faArrowLeft} /> {t('printLead.back')}
        </button>
        <button className="print-button" onClick={handlePrint}>
          <FontAwesomeIcon icon={faPrint} /> {t('printLead.print')}
        </button>
      </div>

      <div className="print-lead-content" ref={componentRef}>
        <div className="print-lead-header">
          <h1>Bodex.se</h1>
          <h2>{address}</h2>
        </div>

        <div className="print-lead-section">
          <h3>{t('printLead.propertyInfo')}</h3>
          <table className="print-lead-table">
            <tbody>
              <tr>
                <td><strong>📍 {t('common.address')}:</strong></td>
                <td>{address}</td>
              </tr>
              <tr>
                <td><strong>🏠 {t('printLead.type')}</strong></td>
                <td>{translatePropertyType(propertyType, language)}</td>
              </tr>
              {numRooms && (
                <tr>
                  <td><strong>🛏️ {t('printLead.rooms')}</strong></td>
                  <td>{numRooms}</td>
                </tr>
              )}
              {area && (
                <tr>
                  <td><strong>📐 {t('printLead.area')}</strong></td>
                  <td>{area} m²</td>
                </tr>
              )}
              {floor && (
                <tr>
                  <td><strong>⬆️ {t('printLead.floor')}</strong></td>
                  <td>{floor}</td>
                </tr>
              )}
              {condition && (
                <tr>
                  <td><strong>🛠️ {t('printLead.condition')}</strong></td>
                  <td>{condition}</td>
                </tr>
              )}
              {constructionYear && (
                <tr>
                  <td><strong>🏗️ {t('printLead.constructionYear')}</strong></td>
                  <td>{constructionYear}</td>
                </tr>
              )}
              {monthlyFee && (
                <tr>
                  <td><strong>💳 {t('printLead.fee')}</strong></td>
                  <td>{monthlyFee} {monthlyFeeSuffix}</td>
                </tr>
              )}
              {plotSize && (
                <tr>
                  <td><strong>🌳 {t('printLead.plotSize')}</strong></td>
                  <td>{plotSize} m²</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {estimatedValue && (
          <div className="print-lead-section valuation-section">
            <h3>📊 {t('printLead.valuation')}</h3>
            <div className="estimated-value">{estimatedValue}</div>
            {explanation && <p className="explanation">{explanation}</p>}
          </div>
        )}

        <div className="print-lead-section no-print">
          <h3>📞 {t('printLead.contactInfo')}</h3>
          <table className="print-lead-table">
            <tbody>
              <tr>
                <td><strong>📱 {t('brokerDashboard.phone')}:</strong></td>
                <td>{phone}</td>
              </tr>
              <tr>
                <td><strong>✉️ {t('common.email')}:</strong></td>
                <td>{email}</td>
              </tr>
            </tbody>
          </table>

          <div className="contact-buttons">
            <a href={`tel:${phone.replace(/\s/g, '').replace(/-/g, '')}`} className="contact-btn call-btn">
              {t('printLead.call')}
            </a>
            <a href={`mailto:${email}`} className="contact-btn email-btn">
              {t('printLead.sendEmail')}
            </a>
          </div>
        </div>

        <div className="print-lead-footer">
          <p>Copyright © {new Date().getFullYear()} Bodex</p>
        </div>
      </div>
    </div>
  );
};

export default PrintLead;

